import {assert, CoordinateLonLat, formatTime} from "../utils.ts";

export type WeatherForecastEntry = {
    time: string,
    temperature: number,
    isDay: boolean,
    code: number,
};

export type WeatherForecast = [
    WeatherForecastEntry,
    WeatherForecastEntry,
    WeatherForecastEntry,
    WeatherForecastEntry,
];

function storeForecast(forecast: WeatherForecast): void {
    localStorage.setItem('weatherData', JSON.stringify(forecast));
    localStorage.setItem('weatherDataLastUpdateTime', new Date().toISOString());
}

function loadSavedForecast(): WeatherForecast | null {
    const forecast = localStorage.getItem('weatherData');
    return forecast === null ? null : JSON.parse(forecast);
}

function isRequiredToUpdateForecast(): boolean {
    const lastUpdateTime = localStorage.getItem('weatherDataLastUpdateTime');

    if (lastUpdateTime === null) {
        return true;
    }

    const oneHourInMilliseconds = 1000 * 60 * 60;
    const lastUpdateTimeDate = new Date(lastUpdateTime);
    return (Date.now() - lastUpdateTimeDate.getTime()) > oneHourInMilliseconds;
}

type WeatherData = {
    time: Date[];
    is_day: boolean[];
    temperature_2m: number[];
    weather_code: number[];
};

async function fetchWeatherData({lon, lat}: CoordinateLonLat): Promise<WeatherData> {
    const endpoint = new URL('https://api.open-meteo.com/v1/forecast');
    endpoint.searchParams.append('latitude', lat.toString());
    endpoint.searchParams.append('longitude', lon.toString());
    endpoint.searchParams.append('hourly', 'temperature_2m,weather_code,is_day');
    endpoint.searchParams.append('models', 'best_match');
    endpoint.searchParams.append('timezone', 'auto');
    endpoint.searchParams.append('forecast_days', '2');

    const response = await fetch(endpoint);
    const {hourly} = await response.json();
    hourly.time = hourly.time.map((time: string) => new Date(time));
    hourly.is_day = hourly.is_day.map((isDay: number) => isDay === 1);
    return hourly as WeatherData;
}

function prepareForecast(weatherData: WeatherData): WeatherForecast {
    function getNowWithoutMinutesAndSeconds(): Date {
        return new Date(new Date().setMinutes(0, 0, 0));
    }

    const loadedEntriesCount = 4;
    const now = getNowWithoutMinutesAndSeconds();
    const currentTimeIndex = weatherData.time.findIndex((time: Date) => time >= now);
    const result = weatherData.time
        .map((time, index) => ({
            time: formatTime(time),
            temperature: weatherData.temperature_2m[index],
            isDay: weatherData.is_day[index],
            code: weatherData.weather_code[index],
        } as WeatherForecastEntry))
        .filter((_, index) => index >= currentTimeIndex && index < currentTimeIndex + loadedEntriesCount);

    assert(result.length === loadedEntriesCount);

    return result as WeatherForecast;
}

export async function getWeatherForecast(coordinate: CoordinateLonLat | null): Promise<WeatherForecast | null> {
    if (!isRequiredToUpdateForecast()) {
        const savedForecast = loadSavedForecast();

        if (savedForecast !== null) {
            return savedForecast;
        }
    }

    if (coordinate === null) {
        return null;
    }

    const forecast = prepareForecast(await fetchWeatherData(coordinate));
    storeForecast(forecast);
    return forecast;
}